import styled from 'styled-components';
import { Container, Page } from '@ui';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import React from 'react';
import { AI_TOOLS } from '@constants';
import { CompanyQuestionPromptsSummary } from './components/CompanyOverview';
import BackArrowButton from '@ui/buttons/BackArrowButton';
import { FormState } from '@ui/FormState';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import { ResultRatingForm } from '@containers/form/ResultRatingForm';

import { AdvancedSettingsButton } from './AdvancedSettings';
import { PROMPT_SERIES_MODES, usePromptSeries } from '@hooks/features/usePromptSeries';
import { CompanySelectionInput } from './components/CompanySelectionInput';

const PageWrapper = styled.div`
  + div {
    width: 100%;
  }
`;
const SubmitButtonWrapper = styled.div`
  text-align: center;
  width: 100%;
`;
const FormWrapper = styled.div`
  input {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 8px;
  }

  select {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 12px 16px;
    -webkit-appearance: auto !important;
  }

  textarea {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    min-height: 100px;
    width: 100%;
    padding: 16px;
  }
`;

const tool = AI_TOOLS.WARNING_INDICATORS;

export default function WarningIndicatorsTool({ onGoBack }) {
  const [company, setCompany] = React.useState(null);
  const [companyName, setCompanyName] = React.useState('');

  const { resultId, resultIdTicker, questionList, formError, onSubmit, onClear } = usePromptSeries(tool);

  React.useEffect(() => {
    if (resultId) {
      const id = 'snapshot-answers-section';
      const yOffset = -26;
      const element = document.getElementById(id);
      if (element) {
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

        setTimeout(() => {
          window.scrollTo({ top: y, behavior: 'smooth' });
        }, 300);
      }
    }
  }, [resultId]);

  const onGetWarningIndicators = () => {
    onSubmit(company, PROMPT_SERIES_MODES.warningIndicators);
  };

  const onAddTicker = (t) => {
    setCompany(t);
    setCompanyName(t.name);
    onClear();
  };

  const onRemoveTicker = () => {
    setCompany(null);
  };

  const handleClear = () => {
    onClear();
  };

  return (
    <PageWrapper>
      <Page width={resultId ? '1000px' : '800px'}>
        <BackArrowButton onClick={onGoBack} />
        <AdvancedSettingsButton tool={tool} />
        <Container top={40}>
          <FormWrapper>
            <Container top={24}>
              <CompanySelectionInput
                value={company}
                label={null}
                onAddCompany={onAddTicker}
                onRemoveCompany={onRemoveTicker}
                placeholder={'Search Companies / Tags'}
                alwaysHideClear
              />
            </Container>
            <Container top={24}>
              <Container centerAll>
                <FormState error={formError} />
              </Container>
              <SubmitButtonWrapper>
                <FlatButton disabled={company === null} onClick={onGetWarningIndicators} fullWidth>
                  Get Warning Indicators
                </FlatButton>
              </SubmitButtonWrapper>
              {resultId ? (
                <Container top={8}>
                  <SubmitButtonWrapper>
                    <TransparentFlatButton onClick={handleClear} fullWidth>
                      Clear
                    </TransparentFlatButton>
                  </SubmitButtonWrapper>
                </Container>
              ) : null}
            </Container>
          </FormWrapper>
        </Container>
      </Page>
      {resultId && (
        <Page width={'1000px'}>
          <div id={'snapshot-answers-section'} />
          <CompanyQuestionPromptsSummary
            questionList={questionList}
            companyName={companyName}
            ticker={resultIdTicker}
            resultId={resultId}
            isRedFlags
          />
          <ResultRatingForm resultId={resultId} />

          <Container top={24} centerAll>
            <SkeletonButton onClick={handleClear}>Close Section</SkeletonButton>
          </Container>
        </Page>
      )}
    </PageWrapper>
  );
}
